import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AccountDto } from 'src/app/core/data-models/user.data.models';
import { UserDataService } from 'src/app/core/data-services/identity/user.data.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { AttachmentService } from 'src/app/core/data-services/attachments/attachment.service';

interface AccountSettingTab {
  name: string;
  icon: string;
  isVisible: (account?: AccountDto) => boolean;
}

@Component({
  selector: 'app-account-setting',
  templateUrl: './account-setting.component.html',
  styleUrls: ['./account-setting.component.css'],
})
export class AccountSettingComponent implements OnInit  {
  selectedTap = 'accountInformation';
  accoutDto?: AccountDto;
  profileImageUrl = '';
  defaultAvatar = 'assets/images/user.svg';
  uploadingAvatar = false;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  taps: AccountSettingTab[] = [
    {
      name: 'accountInformation',
      icon: 'fa fa-user',
      isVisible: (account?: AccountDto) => {
        if (!account) return false;
        if (typeof account.hasNicInformation === 'boolean') {
          return account.hasNicInformation;
        }
        return !!(account.fullName || account.birthdate);
      }
    },
    {
      name: 'companyInformation',
      icon: 'fa fa-building',
      isVisible: (account?: AccountDto) => {
        if (!account) return false;
        if (typeof account.hasCompany === 'boolean') {
          return account.hasCompany;
        }
        return !!account.companyName;
      }
    },
    { name: 'bankAccount', icon: 'fa fa-credit-card', isVisible: () => true },
    { name: 'changePassword', icon: 'fa fa-lock', isVisible: () => true },
    { name: 'editEmail', icon: 'fa fa-envelope', isVisible: () => true },
    { name: 'editPhoneNumber', icon: 'fa fa-phone', isVisible: () => true },
  ];

  constructor(
    private translateService: TranslateService,
    private userDataService: UserDataService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private attachmentService: AttachmentService,
  ) {}

 ngOnInit() {
       const fragment = this.route.snapshot.fragment;
       if (fragment && this.taps.some((tap) => tap.name === fragment)) {
         this.selectedTap = fragment;
       }
       this.route.fragment.subscribe((value) => {
         if (value && this.taps.some((tap) => tap.name === value)) {
           this.selectedTap = value;
         }
       });
       this.loadAccountData();
}

  get visibleTaps(): AccountSettingTab[] {
    return this.taps.filter((tap) => tap.isVisible(this.accoutDto));
  }

  loadAccountData() {
    this.userDataService.GetMyAccounts().subscribe({
      next: (res) => {
        const user = res?.resource ?? res?.data;
        if (user) {
          this.accoutDto = user;
          this.profileImageUrl = user.profileImage ?? '';
          this.resolveProfileImageIfNeeded();
          if (!this.visibleTaps.some((tap) => tap.name === this.selectedTap)) {
            this.selectedTap = this.visibleTaps[0]?.name ?? 'bankAccount';
          }
        }
      },
      error: (err) => {
        console.error('Error loading account data', err);
      }
    });
  }

  private toProfileImageUrl(path: string | null | undefined): string {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(path)) {
      return path;
    }
    const base = (environment as any).ossBaseUrl ?? '';
    return base ? (base.endsWith('/') ? base + path : base + '/' + path) : path;
  }

  get profileImageDisplay(): string {
    const path = this.profileImageUrl || this.accoutDto?.profileImage;
    if (!path || path === null || path === '') return this.defaultAvatar;
    const fullUrl = this.toProfileImageUrl(path);
    return fullUrl || this.defaultAvatar;
  }

  private resolveProfileImageIfNeeded(): void {
    const path = this.profileImageUrl;
    if (!path || path.startsWith('http://') || path.startsWith('https://')) return;
    if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(path)) return;
    this.attachmentService.getViewUrl(path).then((url) => {
      this.profileImageUrl = url;
    }).catch(() => {});
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = this.defaultAvatar;
    }
  }

  triggerAvatarInput(): void {
    this.fileInput?.nativeElement?.click();
  }

  onAvatarFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      this.notificationService.error('Please select a valid image (JPEG, PNG, GIF or WebP).');
      return;
    }
    if (file.size > maxSize) {
      this.notificationService.error('Image size must be less than 5MB.');
      return;
    }
    this.uploadingAvatar = true;
    this.attachmentService.uploadFile(file, 101).then((uploaded) => {
      this.userDataService.ChangeProfileImageUrl({ storedFileId: uploaded.storedFileId }).subscribe({
        next: () => {
          this.uploadingAvatar = false;
          this.loadAccountData();
          this.notificationService.success('Profile photo updated successfully.');
          input.value = '';
        },
        error: () => {
          this.uploadingAvatar = false;
          this.notificationService.error('Failed to update profile photo.');
          input.value = '';
        }
      });
    }).catch(() => {
      this.uploadingAvatar = false;
      this.notificationService.error('Failed to update profile photo.');
      input.value = '';
    });
  }

  selectTap(tapName: string) {
    if (!this.visibleTaps.some((tap) => tap.name === tapName)) {
      return;
    }
    this.selectedTap = tapName;
  }
}
